(ns practitest.frontend.milestone.readiness-index
  (:require [practitest.frontend.components2.dashboard.chart :as chart]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.common.ui :refer [nbsp]]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.milestone.subs :as-alias milestones.subs]
            [practitest.frontend.milestone.readiness-index.subs :as ri.subs]
            [practitest.frontend.milestone.events :as milestone.events]
            [practitest.frontend.components2.tooltip :as tu]
            [practitest.frontend.milestone.translations :refer [t] :as tr]
            [practitest.frontend.components2.confirmation-modal :refer [confirmation-modal]]
            [re-frame.core :as rf]))

;; RRQI - Release Readiness Quality Index
(defn planned-vs [{:milestone/keys [start-date end-date]
                   :metrics/keys   [executed-per-day]}]
  (when (and start-date end-date)
    {:chart/type ::chart/vega
     :mark       :line
     :width      400
     :height     400
     :data       {:executed executed-per-day
                  :planned  [{:date (.getTime start-date) :value 0}
                             {:date (.getTime end-date) :value 1}]}
     :encode     {:x {:field :date
                      :type  :temporal
                      :axis  {:label-angle -40
                              :tick-count  10
                              :format      "%y-%m-%d"}}
                  :y {:field :value
                      :axis  {:grid true}
                      :type  :quantitative}}}))

(defn delete-confirmation [{:milestone/keys [linked? name id]}]
  [confirmation-modal
   {:title                    (t "milestone.delete-confirmation.title")
    :confirmation-button-text (t "generic.button.delete")
    :cancel-button-text       (t "generic.button.cancel")
    :on-confirm               #(rf/dispatch [::milestone.events/delete-confirmed id])
    :on-cancel                #(rf/dispatch [::milestone.events/delete-canceled id])
    :dialog-test-id           "delete-confirm-dialog"
    :confirm-test-id          "delete-confirm-btn"}
   [:div.delete-modal-body
    (if linked?
      [:<>
       [:p (t "milestones.linked.delete-confirmation")]
       [:p (t "milestones.want-to-proceed")]
       [:b (t "milestones.value-to-delete")] nbsp name]
      (t "milestones.delete-confirmation" {:milestone name}))]])

(defn icon [score-level]
  (when score-level
    (let [icon-radius 7.5
          half        (* icon-radius 0.5)
          tri         (* icon-radius 1.17)]
      (case score-level
        :high
        [:g.label-icon
         [:circle.label-icon-circle {:r icon-radius}]
         [:text.label-icon-text "\u2713"]]
        :medium
        [:g.label-icon
         [:circle.label-icon-circle {:r icon-radius}]
         [:line.label-icon-dash {:x1 (- half) :y1 0 :x2 half :y2 0}]]
        :low
        [:g.label-icon
         [:polygon.label-icon-triangle
          {:points (str "0," (- tri) " " tri "," half " -" tri "," half)}]
         [:text.label-icon-text "!"]]))))

(defn metric-card [{:keys [score-level score label url]}]
  [:a.metric-card {:class score-level :href url}
   [:div.metric-value (or score "N/A")]
   [:div.metric-label label]])

(defn formatted-period [{:milestone/keys [start-date end-date]}]
  (t "milestone.period" {:from (fu/formatted-local-date start-date)
                         :to   (fu/formatted-local-date end-date)}))

(defn formatted-last-update [{:milestone/keys [updated-at]}]
  (t "milestone.last-update" {:last-update (fu/formatted-local-date updated-at)}))

(defn badge-title [{::keys [badge]}]
  (let [{:keys [text tooltip]} badge]
    (if tooltip
      [tu/tooltip [:span.text text] tooltip]
      [:span.text text])))

(defn badge [{:milestone/keys [upcoming?] :as milestone}]
  (let [popover-text [:span "Readiness score is based on Coverage, Execution, 
and Remaining Defects. Weights shift during the sprint, giving
 higher weight to defects toward the end." [:br] [:span "Learn more in the"]]
        url "https://www.practitest.com/help/data-management-and-analysis/release-readiness-index/"]
    (when-not upcoming?
      [:div.ri-badge
       [tu/popover
        (fn [props] [icons/mux-info-circle props])
        [:span popover-text nbsp
         [:a {:href url :target "_"} " help docs"]]]
       (badge-title milestone)])))

(defn card [{:keys [id]}]
  (let [milestone @(rf/subscribe [::ri.subs/milestone id])
        {ms-name         :name
         :keys           [metrics url visible? delete-confirmation-required?]
         ::keys          [rrqi-level data-unavailable?]
         :milestone/keys [archived?]
         :metrics/keys   [rrqi]}
        milestone]
    [:div.readiness-card
     (dom/add-test-id
       {:class [rrqi-level
                (when archived? "archived")
                (when data-unavailable? "not-available")]
        :data-milestone-id id}
       "milestone-card")
     [:div.header
      [:div.title-section
       [:div.title
        [:a.undecorated-link (dom/add-test-id {:href url} "milestone-card-name")
         [:span ms-name]] " |" nbsp
        [:span.status (:milestone/status milestone)]]
       [badge milestone]]
      [:div.sub-title (formatted-period milestone)]
      [:div.last-updated
       [icons/small-clock]
       [:span.text (formatted-last-update milestone)]]]
     [:div.side-by-side.content
      (when visible?
        [:<>
         [:div.left
          [:f> chart/svg {:chart/type     ::chart/readiness-gauge
                          :rrqi           rrqi
                          :not-available? data-unavailable?
                          :icon           (icon rrqi-level)}]
          [:div.readiness-metrics
           (for [metric metrics]
             ^{:key metric}
             [metric-card metric])]]
         [:div.right.readiness-plan
          (when-let [plan-chart (planned-vs milestone)]
            [:f> chart/svg plan-chart])]
         (when delete-confirmation-required?
           [delete-confirmation milestone])])]]))
